<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Categorías de Activos"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre o descripción..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-tag-fill"></i>
          <span class="btn-header-texto ms-2">Nueva categoría</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- ── Tabla escritorio ───────────────────────────────────── -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="categoriasParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="descripcion"
                label="Descripción"
                :filterable="false"
                :items="categoriasParaFiltro"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="responsiva_label"
                label="Responsiva"
                :sortable="false"
                :items="categoriasParaFiltro"
                :modelValue="filtroResponsivaSet"
                @update:modelValue="filtroResponsivaSet = $event"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estado_label"
                label="Estado"
                :sortable="false"
                :items="categoriasParaFiltro"
                :modelValue="filtroEstadoSet"
                @update:modelValue="filtroEstadoSet = $event"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cat in categoriasPaginadas" :key="cat.id">
            <td class="ps-4">
              <span class="fw-bold text-cafe">{{ cat.nombre }}</span>
            </td>
            <td>
              <span class="text-muted-divisan">{{ cat.descripcion || "—" }}</span>
            </td>
            <td class="text-center">
              <span :class="['badge-responsiva', cat.requiere_responsiva ? 'si' : 'no']">
                <i
                  :class="
                    cat.requiere_responsiva
                      ? 'bi bi-file-earmark-check-fill me-1'
                      : 'bi bi-dash-circle me-1'
                  "
                ></i>
                {{ cat.requiere_responsiva ? "Sí" : "No" }}
              </span>
            </td>
            <td class="text-center">
              <span :class="['badge-estado', cat.activo ? 'activo' : 'inactivo']">
                {{ cat.activo ? "Activa" : "Inactiva" }}
              </span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(cat)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarDesactivar(cat)"
                  title="Desactivar"
                  :disabled="!cat.activo"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="categoriasPaginadas.length === 0">
            <td colspan="5" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron resultados
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de
            {{ categoriasFiltradas.length }} categorías</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in opcionesPorPagina" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Cards móvil ────────────────────────────────────────── -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <div
        v-else
        v-for="cat in categoriasPaginadas"
        :key="'mob-' + cat.id"
        class="divisan-mobile-card mb-2 px-3 py-2 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fw-bold text-cafe">{{ cat.nombre }}</span>
          <div class="d-flex gap-2">
            <button class="btn-circular-sm" @click="abrirModal(cat)" title="Editar">
              <i class="bi bi-pencil-square text-verde"></i>
            </button>
            <button
              class="btn-circular-sm"
              @click="confirmarDesactivar(cat)"
              title="Desactivar"
              :disabled="!cat.activo"
            >
              <i class="bi bi-trash3-fill text-danger"></i>
            </button>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <span :class="['badge-responsiva', cat.requiere_responsiva ? 'si' : 'no']">
            <i
              :class="
                cat.requiere_responsiva
                  ? 'bi bi-file-earmark-check-fill me-1'
                  : 'bi bi-dash-circle me-1'
              "
            ></i>
            {{ cat.requiere_responsiva ? "Requiere responsiva" : "Sin responsiva" }}
          </span>
          <span :class="['badge-estado', cat.activo ? 'activo' : 'inactivo']">
            {{ cat.activo ? "Activa" : "Inactiva" }}
          </span>
        </div>
        <div v-if="cat.descripcion" class="mt-1">
          <small class="text-muted-divisan">{{ cat.descripcion }}</small>
        </div>
      </div>

      <!-- Paginación móvil -->
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ categoriasFiltradas.length }}</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in opcionesPorPagina" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal crear / editar ───────────────────────────────── -->
    <div
      class="modal fade"
      id="modalCategoriaActivo"
      tabindex="-1"
      aria-hidden="true"
      ref="modalElement"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar categoría" : "Nueva categoría" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <form @submit.prevent="guardarCategoria">
            <div class="modal-body">
              <!-- Nombre -->
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Nombre <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Hardware, Oficina, Transporte..."
                  maxlength="100"
                />
              </div>

              <!-- Descripción -->
              <div class="mb-3">
                <label class="form-label label-divisan">Descripción</label>
                <textarea
                  v-model="form.descripcion"
                  class="form-control divisan-input-modal"
                  rows="2"
                  placeholder="Descripción opcional de la categoría..."
                ></textarea>
              </div>

              <!-- Requiere responsiva -->
              <div class="mb-3">
                <label class="form-label label-divisan d-block mb-2">Responsiva</label>
                <div
                  class="toggle-wrapper"
                  @click="form.requiere_responsiva = !form.requiere_responsiva"
                >
                  <div
                    :class="['toggle-track', { 'toggle-on': form.requiere_responsiva }]"
                  >
                    <div class="toggle-thumb"></div>
                  </div>
                  <span class="toggle-label">
                    {{
                      form.requiere_responsiva
                        ? "Esta categoría requiere responsiva PDF"
                        : "Esta categoría no requiere responsiva"
                    }}
                  </span>
                </div>
                <small class="text-muted-divisan mt-1 d-block">
                  <i class="bi bi-info-circle me-1"></i>
                  Activa si los activos de esta categoría son asignados personalmente y
                  requieren firma de responsiva.
                </small>
              </div>
            </div>

            <div class="modal-footer border-0 pt-0">
              <button type="button" class="btn btn-cancelar me-2" data-bs-dismiss="modal">
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-nuevo flex-grow-1 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar categoría" : "Guardar categoría" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getCategoriasActivo,
  crearCategoriaActivo,
  actualizarCategoriaActivo,
  eliminarCategoriaActivo,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

// ── Estado ──────────────────────────────────────────────────────
const categorias = ref([]);
const filtro = ref("");
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const modalElement = ref(null);
const paginaActual = ref(1);
const porPagina = ref(10);
const opcionesPorPagina = [5, 10, 15, 20];
const filtroResponsivaSet = ref(new Set());
const filtroEstadoSet = ref(new Set());
let modalInstance = null;

const form = ref({
  id: null,
  nombre: "",
  descripcion: "",
  requiere_responsiva: false,
});

watch([filtro, filtroResponsivaSet, filtroEstadoSet], () => {
  paginaActual.value = 1;
});

// ── Ordenamiento ─────────────────────────────────────────────────
const sortKey = ref("nombre");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

// ── Computed ─────────────────────────────────────────────────────
const categoriasBuscadas = computed(() => {
  const s = filtro.value.toLowerCase();
  return categorias.value.filter(
    (c) =>
      c.nombre.toLowerCase().includes(s) ||
      (c.descripcion && c.descripcion.toLowerCase().includes(s))
  );
});

const categoriasParaFiltro = computed(() =>
  categoriasBuscadas.value.map((c) => ({
    ...c,
    responsiva_label: c.requiere_responsiva ? "Sí" : "No",
    estado_label: c.activo ? "Activa" : "Inactiva",
  }))
);

const categoriasFiltradas = computed(() => {
  const filtradas = categoriasParaFiltro.value.filter(
    (c) =>
      (!filtroResponsivaSet.value.size ||
        filtroResponsivaSet.value.has(c.responsiva_label)) &&
      (!filtroEstadoSet.value.size || filtroEstadoSet.value.has(c.estado_label))
  );
  return [...filtradas].sort((a, b) => {
    let va = a[sortKey.value] ?? "";
    let vb = b[sortKey.value] ?? "";
    const cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base" });
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(categoriasFiltradas.value.length / porPagina.value))
);

const categoriasPaginadas = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return categoriasFiltradas.value.slice(inicio, inicio + porPagina.value);
});

const rangoInicio = computed(() =>
  categoriasFiltradas.value.length === 0
    ? 0
    : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, categoriasFiltradas.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  porPagina.value = n;
  paginaActual.value = 1;
}

// ── API ──────────────────────────────────────────────────────────
async function cargarCategorias() {
  loading.value = true;
  try {
    const data = await getCategoriasActivo();
    categorias.value = Array.isArray(data) ? data : data.results ?? [];
  } catch {
    Swal.fire("Error", "No se pudieron cargar las categorías", "error");
  } finally {
    loading.value = false;
  }
}

// ── Modal ────────────────────────────────────────────────────────
function abrirModal(cat) {
  if (cat) {
    editando.value = true;
    form.value = {
      id: cat.id,
      nombre: cat.nombre,
      descripcion: cat.descripcion || "",
      requiere_responsiva: cat.requiere_responsiva ?? false,
    };
  } else {
    editando.value = false;
    form.value = { id: null, nombre: "", descripcion: "", requiere_responsiva: false };
  }
  modalInstance.show();
}

async function guardarCategoria() {
  guardando.value = true;
  try {
    const payload = {
      nombre: form.value.nombre.trim(),
      descripcion: form.value.descripcion.trim() || null,
      requiere_responsiva: form.value.requiere_responsiva,
    };
    if (editando.value) {
      await actualizarCategoriaActivo(form.value.id, payload);
    } else {
      await crearCategoriaActivo(payload);
    }
    await cargarCategorias();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Categoría ${editando.value ? "actualizada" : "creada"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    const detalle = err?.response?.data;
    const mensaje =
      detalle?.nombre?.[0] ||
      detalle?.non_field_errors?.[0] ||
      "No se pudo guardar la categoría";
    Swal.fire("Error", mensaje, "error");
  } finally {
    guardando.value = false;
  }
}

// ── Borrado lógico ───────────────────────────────────────────────
async function confirmarDesactivar(cat) {
  const res = await Swal.fire({
    title: `¿Desactivar "${cat.nombre}"?`,
    text: "Esta categoría dejará de estar disponible al registrar activos.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (!res.isConfirmed) return;
  try {
    await eliminarCategoriaActivo(cat.id);
    await cargarCategorias();
    Swal.fire({
      icon: "success",
      title: "Desactivada",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo desactivar la categoría", "error");
  }
}

// ── Lifecycle ────────────────────────────────────────────────────
onMounted(() => {
  cargarCategorias();
  modalInstance = new Modal(modalElement.value);
});
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-divisan {
  color: rgba(64, 34, 8, 0.6);
  font-weight: 500;
  font-size: 0.85rem;
}

/* ── Botones ── */
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-cancelar {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  font-weight: 600;
  border-radius: 8px;
  padding: 0.5rem 1rem;
}
.btn-cancelar:hover {
  background: rgba(96, 108, 56, 0.08);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* ── Tabla ── */
.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 15px;
  font-weight: 800;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 15px;
}
/* ── Badges ── */
.badge-responsiva {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}
.badge-responsiva.si {
  background-color: rgba(96, 108, 56, 0.12);
  color: var(--d-green-dark);
  border: 1px solid rgba(96, 108, 56, 0.3);
}
.badge-responsiva.no {
  background-color: rgba(64, 34, 8, 0.06);
  color: #8b7355;
  border: 1px solid rgba(64, 34, 8, 0.15);
}
.badge-estado {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.activo {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.inactivo {
  background-color: var(--d-brown);
  color: var(--d-cream);
}

/* ── Toggle ── */
.toggle-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}
.toggle-track {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: rgba(64, 34, 8, 0.15);
  border: 1.5px solid rgba(64, 34, 8, 0.2);
  position: relative;
  transition: all 0.25s;
  flex-shrink: 0;
}
.toggle-track.toggle-on {
  background: var(--d-green);
  border-color: var(--d-green);
}
.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s;
}
.toggle-track.toggle-on .toggle-thumb {
  transform: translateX(20px);
}
.toggle-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--d-brown);
}

/* ── Botones circulares ── */
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
  cursor: pointer;
}
.btn-circular:hover:not(:disabled) {
  background: rgba(96, 108, 56, 0.12);
}
.btn-circular:disabled {
  opacity: 0.35;
  cursor: default;
}
.btn-circular-sm {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.8rem;
}
.btn-circular-sm:hover:not(:disabled) {
  background: rgba(96, 108, 56, 0.1);
}
.btn-circular-sm:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Paginación ── */
.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

/* ── Móvil ── */
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

/* ── Modal ── */
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
  color: var(--d-brown);
}
.divisan-input-modal:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 0.2rem rgba(96, 108, 56, 0.2);
}

@media (max-width: 991.98px) {
  :deep(.modal) {
    z-index: 1200;
  }
  :deep(.modal-backdrop) {
    z-index: 1199;
  }
  :deep(.modal-dialog) {
    margin-top: 75px;
  }
}
@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
